/**
 * Status-page incident state — backs the shell incident banner.
 *
 * Faithful port of the legacy `IncidentStatusService`
 * (`raptor-shell-ui/src/app/core/services/incident-status.service.ts`):
 *   - `normalizeIncidents` accepts either a bare array or `{ incidents: [] }`
 *     and reconciles snake_case / camelCase fields from the status-page API.
 *   - per-day dismissal is fingerprinted by the latest update timestamp and
 *     persisted in localStorage under the SAME key the Angular shell uses
 *     (`statusPageIncidentDismissal`), so dismissing in one shell carries to
 *     the other during the canary.
 *
 * Live updates arrive via the SignalR `portalincidentstatusmethod` hub
 * (see `incidents-bridge.ts`); the initial list is fetched once on start.
 */

import { create } from 'zustand';

/** localStorage key — byte-exact parity with legacy `LOCAL_STORAGE.STATUS_PAGE_DISMISSAL`. */
const DISMISSAL_KEY = 'statusPageIncidentDismissal';

export interface IncidentUpdateView {
  status?: string;
  body?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IncidentView {
  id?: string;
  name?: string;
  status?: string;
  impact?: string;
  createdAt?: string;
  updatedAt?: string;
  updates: IncidentUpdateView[];
}

interface DismissalState {
  latestUpdateFingerprint: number;
  dismissedOn: number;
}

interface RawIncidentUpdate {
  status?: string;
  body?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

interface RawIncident {
  id?: string;
  name?: string;
  status?: string;
  impact?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  incident_updates?: RawIncidentUpdate[];
}

/**
 * Reconcile the status-page payload (array or `{ incidents }`) into
 * `IncidentView[]`, mirroring legacy `normalizeIncidents`.
 */
export function normalizeIncidents(payload: unknown): IncidentView[] {
  const container = payload as { incidents?: unknown } | unknown[] | null;
  const items = Array.isArray(container) ? container : (container?.incidents ?? []);
  if (!Array.isArray(items)) return [];

  return (items as RawIncident[]).map((item) => {
    const updates = Array.isArray(item?.incident_updates)
      ? item.incident_updates.map(
          (update): IncidentUpdateView => ({
            status: update?.status,
            body: update?.body,
            createdAt: update?.created_at ?? update?.createdAt,
            updatedAt: update?.updated_at ?? update?.updatedAt,
          }),
        )
      : [];

    return {
      id: item?.id,
      name: item?.name,
      status: item?.status,
      impact: item?.impact,
      createdAt: item?.created_at ?? item?.createdAt,
      updatedAt: item?.updated_at ?? item?.updatedAt,
      updates,
    };
  });
}

function startOfDay(date: Date): number {
  const clone = new Date(date);
  clone.setHours(0, 0, 0, 0);
  return clone.getTime();
}

/** Fingerprint the incident set by its most-recent update (legacy `buildDismissalState`). */
function buildDismissalState(incidents: IncidentView[]): DismissalState | undefined {
  if (!Array.isArray(incidents) || incidents.length === 0) return undefined;

  const latestUpdatedAt = incidents
    .map((incident) => incident.updatedAt || incident.createdAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value).getTime())
    .filter((timestamp) => !Number.isNaN(timestamp))
    .sort((a, b) => b - a)[0];

  if (!latestUpdatedAt) return undefined;

  return { latestUpdateFingerprint: latestUpdatedAt, dismissedOn: startOfDay(new Date()) };
}

function readDismissal(): DismissalState | undefined {
  try {
    const raw = localStorage.getItem(DISMISSAL_KEY);
    return raw ? (JSON.parse(raw) as DismissalState) : undefined;
  } catch {
    return undefined;
  }
}

/** Persist today's dismissal for the current incident set (legacy `persistDismissalState`). */
export function persistDismissal(incidents: IncidentView[]): void {
  const state = buildDismissalState(incidents);
  try {
    if (!state) {
      localStorage.removeItem(DISMISSAL_KEY);
      return;
    }
    localStorage.setItem(DISMISSAL_KEY, JSON.stringify(state));
  } catch {
    /* private mode — best effort */
  }
}

/**
 * True when the banner should be shown for this incident set: there is a fresh
 * update fingerprint, or the user has not dismissed it today. Mirrors legacy
 * `shouldShowStatusToast`.
 */
export function shouldShowBanner(incidents: IncidentView[]): boolean {
  const state = buildDismissalState(incidents);
  if (!state) return false;

  const stored = readDismissal();
  if (!stored) return true;
  if (stored.latestUpdateFingerprint !== state.latestUpdateFingerprint) return true;
  return stored.dismissedOn !== startOfDay(new Date());
}

interface IncidentState {
  incidents: IncidentView[];
  /** Hidden for this session — either dismissed or not due to be shown. */
  dismissed: boolean;
  setIncidents: (incidents: IncidentView[]) => void;
  dismiss: () => void;
}

export const useIncidentStore = create<IncidentState>((set, get) => ({
  incidents: [],
  dismissed: true,
  setIncidents: (incidents) =>
    // A new fingerprint re-shows the banner even if dismissed earlier today.
    set({ incidents, dismissed: !shouldShowBanner(incidents) }),
  dismiss: () => {
    persistDismissal(get().incidents);
    set({ dismissed: true });
  },
}));
